import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminFetchRetryConfigTemplates,
  AdminUpdateRetryConfigTemplate,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type { RetryActionType } from 'src/constants/scheduler/enums'
import type {
  RetryConfigTemplate,
  UpdateRetryConfigTemplatePayload,
} from 'src/interfaces/retryConfigTemplate'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

/**
 * The saga's own view of the payload: `retryActionType` is optional here because the handler
 * guards it at runtime — an action can be dispatched with a malformed payload from anywhere.
 * The exported type stays strict for the modal that builds it. Same split as `onFetchMarkets`.
 */
type UpdateTemplateActionPayload = Omit<UpdateRetryConfigTemplatePayload, 'retryActionType'> & {
  retryActionType?: RetryActionType
}

export function* onFetchRetryConfigTemplates(action: AsyncAction<undefined>) {
  try {
    // Answers a raw array. The optional `?retryActionType=` filter is not sent — the catalog is
    // small and the tab always shows all of it.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchRetryConfigTemplates,
      { method: 'GET' },
    )

    const templates = normalizeApiListPayload<RetryConfigTemplate>(payload)

    yield put(AdminFetchRetryConfigTemplates.success(templates))

    if (action.next) {
      action.next(null, templates)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.retryConfigTemplate.onFetchRetryConfigTemplates] error',
    })

    yield put(AdminFetchRetryConfigTemplates.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load retry config templates'))
    }
  }
}

export function* onUpdateRetryConfigTemplate(action: AsyncAction<UpdateTemplateActionPayload>) {
  const retryActionType = action.payload?.retryActionType

  if (!retryActionType) {
    yield put(AdminUpdateRetryConfigTemplate.failed(new Error('Missing retryActionType')))

    if (action.next) {
      action.next('Missing retryActionType')
    }

    return
  }

  const { maxAttempts, backoffMultiplier, timeoutMinutes } = action.payload

  try {
    // Keyed by `type`, not `id` — the route is `/templates/:retryActionType`.
    const { payload }: { payload: RetryConfigTemplate } = yield call(
      callSecureApi,
      Endpoints.AdminUpdateRetryConfigTemplate,
      {
        method: 'PUT',
        params: [{ field: ':retryActionType', value: retryActionType }],
        body: { maxAttempts, backoffMultiplier, timeoutMinutes },
      },
    )

    yield put(AdminUpdateRetryConfigTemplate.success(payload))

    yield put(AdminFetchRetryConfigTemplates.request())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.retryConfigTemplate.onUpdateRetryConfigTemplate] error',
    })

    yield put(AdminUpdateRetryConfigTemplate.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update the retry config template'))
    }
  }
}

export function* retryConfigTemplateSaga() {
  yield takeLatest(AdminFetchRetryConfigTemplates.type.REQUEST, onFetchRetryConfigTemplates)
  yield takeEvery(AdminUpdateRetryConfigTemplate.type.REQUEST, onUpdateRetryConfigTemplate)
}
